import { useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisVertical } from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import Popover from './Popover';

/**
 * Cockpit KebabMenu — ⋮ quick actions. The menu renders through
 * ui/Popover (portaled) so it escapes table overflow clipping and
 * stacking contexts.
 */
export interface KebabAction {
  label: string;
  icon?: IconDefinition;
  danger?: boolean;
  /** Shown dimmed and inert — for actions that have nothing to act on right now. */
  disabled?: boolean;
  onClick: () => void;
}

export interface KebabMenuProps {
  actions: Array<KebabAction | null | false | undefined>;
  label?: string;
}

export default function KebabMenu({ actions, label = 'Actions' }: KebabMenuProps) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const items = actions.filter(Boolean) as KebabAction[];

  return (
    <span style={{ position: 'relative', display: 'inline-flex' }} onClick={(e) => e.stopPropagation()}>
      <button
        ref={buttonRef}
        className="pressable hover-wash ck-kebab"
        aria-label={label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '26px',
          height: '26px',
          borderRadius: 'var(--radius-sm)',
          border: 'none',
          background: open ? 'var(--surface-active)' : 'transparent',
          color: 'var(--text-muted)',
          cursor: 'pointer',
        }}
      >
        <FontAwesomeIcon icon={faEllipsisVertical} style={{ fontSize: '13px' }} />
      </button>
      <Popover
        open={open}
        anchorEl={buttonRef.current}
        onClose={() => setOpen(false)}
        align="right"
        minWidth={184}
        role="menu"
        style={{ display: 'flex', flexDirection: 'column' }}
      >
        {items.map((a) => (
          <button
            key={a.label}
            className={a.disabled ? undefined : 'hover-wash pressable'}
            role="menuitem"
            disabled={a.disabled}
            aria-disabled={a.disabled}
            onClick={(e) => {
              e.stopPropagation();
              if (a.disabled) return;
              setOpen(false);
              a.onClick();
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '9px',
              padding: '7px 10px',
              border: 'none',
              background: 'transparent',
              borderRadius: 'var(--radius-sm)',
              cursor: a.disabled ? 'not-allowed' : 'pointer',
              opacity: a.disabled ? 0.45 : 1,
              textAlign: 'left',
              fontFamily: 'var(--font-ui)',
              fontSize: 'var(--text-sm)',
              color: a.danger ? 'var(--danger-500)' : 'var(--text-body)',
              whiteSpace: 'nowrap',
            }}
          >
            {a.icon ? <FontAwesomeIcon icon={a.icon} style={{ fontSize: '12px', width: '15px', color: 'inherit' }} /> : null}
            {a.label}
          </button>
        ))}
      </Popover>
    </span>
  );
}
